import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { setupDefaultCustomerId, useSetupReturn } from "@/lib/setupFlow";
import api from "@/lib/api";
import {
  fmtTRY, fmtDate,
  JOB_STATUS_LABEL, PAYMENT_STATUS_LABEL,
} from "@/lib/format";
import { PageHeader, SectionCard, EmptyState, StatusBadge, StatCard, StickyPageHead } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Search, Pencil, Trash2, Wallet, MoreVertical } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import PaymentsDialog from "@/components/PaymentsDialog";
import JobFormDialog from "@/components/JobFormDialog";
import DateRangeFilter, { isDateInRange } from "@/components/DateRangeFilter";

export default function Jobs() {
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [customerFilter, setCustomerFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [paymentsFor, setPaymentsFor] = useState(null);

  const load = async () => {
    const [j, c] = await Promise.all([
      api.get("/jobs"), api.get("/customers"),
    ]);
    setItems(j.data); setCustomers(c.data);
  };
  useEffect(() => { load(); }, []);

  // Dashboard FAB'dan ``?new=1`` ile geldiyse Yeni İş dialog'unu aç.
  useSetupReturn("job", open);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("new") === "1") {
      setEditing(null); setOpen(true);
      navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return (items ?? []).filter((it) => {
      const matchS = !s || [it.name, it.customer_name, it.description].filter(Boolean).some((v) => String(v ?? "").toLowerCase().includes(s));
      const matchSt = statusFilter === "all" || it.payment_status === statusFilter;
      const matchC = customerFilter === "all" || it.customer_id === customerFilter;
      // Tarih filtresi: iş / satış tarihi ``start_date`` alanı üzerinden
      // kontrol edilir (created_at DEĞİL).  Boş tarih olan bir iş varsa
      // yalnızca filtre aktif olduğunda dışarıda kalır.
      const matchD = isDateInRange(it.start_date, dateRange.from, dateRange.to);
      return matchS && matchSt && matchC && matchD;
    });
  }, [items, search, statusFilter, customerFilter, dateRange]);

  const totals = useMemo(() => {
    // KPI kartları tabloyla senkron olmalı — bu yüzden ``items`` (ham liste)
    // yerine ``filtered`` (tarih + müşteri + ödeme + arama filtrelerinin
    // uygulandığı küme) üzerinden toplanır.  Filtre yokken ``filtered``
    // tüm kayıtlara eşittir → varsayılan davranış korunur.  Satın Almalar
    // ekranındaki server-side ``aggregate`` mantığının client-side eşleniği.
    let sales = 0, collected = 0, outstanding = 0;
    (filtered ?? []).forEach((j) => {
      sales += +j.amount;
      collected += +j.paid_amount;
      outstanding += +j.remaining_amount || 0;
    });
    return { sales, collected, outstanding };
  }, [filtered]);

  const onEdit = (j) => { setEditing(j); setOpen(true); };
  const onDelete = async (j) => {
    if (!window.confirm(`"${j.name}" işi silinsin mi?`)) return;
    await api.delete(`/jobs/${j.id}`);
    toast.success("İş silindi"); load();
  };

  return (
    <div className="space-y-6" data-testid="jobs-page">
      <StickyPageHead>
      <PageHeader title="İşler / Satışlar" subtitle="Müşterilere yapılan tüm işleri ve tahsilatları takip edin"
        action={
          <Button className="bg-slate-900 hover:bg-slate-800 rounded-lg"
                  onClick={() => { setEditing(null); setOpen(true); }}
                  data-testid="add-job-button">
            <Plus className="w-4 h-4 mr-1.5" /> Yeni İş
          </Button>
        }
      />
      </StickyPageHead>

      <JobFormDialog
        open={open}
        onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}
        editing={editing}
        customers={customers}
        defaultCustomerId={setupDefaultCustomerId()}
        onSaved={load}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="Toplam Satış" value={fmtTRY(totals.sales)} accent="slate" testId="jobs-total-sales" />
        <StatCard label="Tahsil Edilen" value={fmtTRY(totals.collected)} accent="emerald" />
        <StatCard label="Bekleyen Alacak" value={fmtTRY(totals.outstanding)} accent="amber" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="İş ara..." className="pl-9 h-10 rounded-lg" data-testid="jobs-search-input" />
        </div>
        <Select value={customerFilter} onValueChange={setCustomerFilter}>
          <SelectTrigger className="w-48 h-10 rounded-lg" data-testid="jobs-customer-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Müşteriler</SelectItem>
            {(customers ?? []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 h-10 rounded-lg" data-testid="jobs-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Ödemeler</SelectItem>
            {Object.entries(PAYMENT_STATUS_LABEL ?? {}).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <DateRangeFilter
          value={dateRange}
          onChange={setDateRange}
          testId="jobs-date-range"
        />
      </div>

      <SectionCard>
        {(filtered ?? []).length === 0 ? <EmptyState title="İş bulunamadı" /> : (
          <>
          <ul className="md:hidden divide-y divide-slate-100" data-testid="job-cards">
            {(filtered ?? []).map((j) => (
              <JobCard key={j.id} j={j} onEdit={() => onEdit(j)}
                       onPayments={() => setPaymentsFor(j)} onDelete={() => onDelete(j)} />
            ))}
          </ul>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/50">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3 font-semibold">İş</th>
                  <th className="px-5 py-3 font-semibold">Müşteri</th>
                  <th className="px-5 py-3 font-semibold">Tarihler</th>
                  <th className="px-5 py-3 font-semibold">Durum</th>
                  <th className="px-5 py-3 font-semibold text-right">Bedel</th>
                  <th className="px-5 py-3 font-semibold text-right">KDV</th>
                  <th className="px-5 py-3 font-semibold text-right">Tahsil</th>
                  <th className="px-5 py-3 font-semibold text-right">Kalan</th>
                  <th className="px-5 py-3 font-semibold">Ödeme</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(filtered ?? []).map((j) => {
                  const kalan = +j.balance_amount || 0;
                  const over = kalan < 0;
                  return (
                    <tr key={j.id} className={over ? "bg-red-50/60 hover:bg-red-50" : "hover:bg-slate-50/60"} data-testid={`job-row-${j.id}`}>
                      <td className="px-5 py-3">
                        <button
                          type="button"
                          onClick={() => onEdit(j)}
                          className="text-left group focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 rounded"
                          data-testid={`job-title-${j.id}`}
                          title="Detayı düzenle"
                        >
                          <div className="font-medium text-slate-900 group-hover:text-slate-900 group-hover:underline cursor-pointer">{j.name}</div>
                          <div className="text-xs text-slate-500 truncate max-w-xs">{j.description || "—"}</div>
                        </button>
                      </td>
                      <td className="px-5 py-3 text-slate-700 hover:underline cursor-pointer" onClick={() => navigate(`/customers/${j.customer_id}`)}>{j.customer_name}</td>
                      <td className="px-5 py-3 text-xs text-slate-600">
                        {j.start_date ? fmtDate(j.start_date) : "—"}<br />
                        {j.end_date ? fmtDate(j.end_date) : <span className="text-slate-400">Devam</span>}
                      </td>
                      <td className="px-5 py-3"><StatusBadge value={j.status} label={JOB_STATUS_LABEL[j.status]} /></td>
                      <td className="px-5 py-3 text-right tabular-nums">{fmtTRY(j.amount)}</td>
                      <td className="px-5 py-3 text-right tabular-nums text-slate-500" data-testid={`job-vat-${j.id}`}>{fmtTRY(j.invoice_vat_total)}</td>
                      <td className="px-5 py-3 text-right tabular-nums text-emerald-600">{fmtTRY(j.paid_amount)}</td>
                      <td className={`px-5 py-3 text-right tabular-nums font-semibold ${over ? "text-red-600" : ""}`} data-testid={`job-remaining-${j.id}`}>{fmtTRY(kalan)}</td>
                      <td className="px-5 py-3"><StatusBadge value={j.payment_status} label={PAYMENT_STATUS_LABEL[j.payment_status]} /></td>
                      <td className="px-5 py-3">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="outline" className="h-8 rounded-lg text-xs" onClick={() => setPaymentsFor(j)} data-testid={`job-payments-${j.id}`}>
                            <Wallet className="w-3.5 h-3.5 mr-1" /> Tahsilatlar
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onEdit(j)} data-testid={`job-edit-${j.id}`}><Pencil className="w-4 h-4" /></Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => onDelete(j)} data-testid={`job-delete-${j.id}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </SectionCard>

      <PaymentsDialog
        key={paymentsFor?.id}
        open={!!paymentsFor}
        onOpenChange={(v) => { if (!v) setPaymentsFor(null); }}
        type="job"
        record={paymentsFor}
        onUpdated={() => load()}
      />
    </div>
  );
}

function JobCard({ j, onEdit, onPayments, onDelete }) {
  const balance = +j.balance_amount || 0;
  const over = balance < 0;
  return (
    <li className={`flex items-start gap-1 pl-4 pr-1 py-2.5 ${over ? "bg-red-50/60" : "active:bg-slate-50"}`} data-testid={`job-card-${j.id}`}>
      <button type="button" onClick={onEdit} className="flex-1 min-w-0 text-left py-0.5" data-testid={`job-card-open-${j.id}`}>
        <div className="flex items-center gap-2 text-[11px] text-slate-500 min-w-0">
          <span className="tabular-nums shrink-0">{j.start_date ? fmtDate(j.start_date) : "—"}</span>
          <StatusBadge value={j.payment_status} label={PAYMENT_STATUS_LABEL[j.payment_status]} />
        </div>
        <div className="font-medium text-slate-900 leading-snug break-words line-clamp-2 mt-0.5">{j.name}</div>
        <div className="text-xs text-slate-500 truncate">{j.customer_name}</div>
        <dl className="mt-1.5 text-[11px] leading-tight space-y-0.5">
          <JobAmount label="Bedel" value={j.amount} className="text-slate-700" testId={`job-card-amount-${j.id}`} />
          <JobAmount label="KDV" value={j.invoice_vat_total} className="text-slate-500" testId={`job-card-vat-${j.id}`} />
          <JobAmount label="Tahsil" value={j.paid_amount} className="text-emerald-600" testId={`job-card-paid-${j.id}`} />
          <JobAmount label="Kalan" value={balance} className={`font-semibold text-xs ${over ? "text-red-600" : "text-slate-900"}`} testId={`job-card-remaining-${j.id}`} />
        </dl>
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="icon" variant="ghost" className="shrink-0 h-10 w-10" aria-label="İşlemler" data-testid={`job-card-menu-${j.id}`}>
            <MoreVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuItem onSelect={onPayments} data-testid={`job-card-payments-${j.id}`}><Wallet className="w-4 h-4 mr-2" /> Tahsilatlar</DropdownMenuItem>
          <DropdownMenuItem onSelect={onEdit} data-testid={`job-card-edit-${j.id}`}><Pencil className="w-4 h-4 mr-2" /> Düzenle</DropdownMenuItem>
          <DropdownMenuItem onSelect={onDelete} className="text-red-600 focus:text-red-600" data-testid={`job-card-delete-${j.id}`}><Trash2 className="w-4 h-4 mr-2" /> Sil</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
}

function JobAmount({ label, value, className, testId }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-slate-500">{label}</dt>
      <dd className={`tabular-nums ${className}`} data-testid={testId}>{fmtTRY(value)}</dd>
    </div>
  );
}
